import { useState } from "react";
import { DateTime } from "luxon";
import type { FilterState, ScopeFilters, PeriodKey } from "@/types/filters";
import {
  getAvailableGranularities,
  getPeriodDateRange,
  getPeriodFamily,
  type Granularity,
} from "@/lib/periods";

const DEFAULT_PERIOD: PeriodKey = "T1W";

// A granularity is only offered when the range covers two or more of its
// buckets, so switching from T52W to MTD can strip the option that was
// selected. Fall back to the longest one still available rather than leaving
// the charts pointed at a grouping that no longer renders.
function clampGranularity(g: Granularity, available: Granularity[]): Granularity {
  if (available.includes(g)) return g;
  return available[available.length - 1] ?? "day";
}

function withPeriodRange(state: FilterState, period: PeriodKey, asOfDate: string): FilterState {
  const range = getPeriodDateRange(period, DateTime.fromISO(asOfDate));
  const available = getAvailableGranularities(range, getPeriodFamily(period));
  return {
    ...state,
    period,
    asOfDate,
    customStart: range.startDate,
    customEnd: range.endDate,
    utilGranularity: clampGranularity(state.utilGranularity, available),
    capGranularity: clampGranularity(state.capGranularity, available),
    revGranularity: clampGranularity(state.revGranularity, available),
    revMetricGranularity: clampGranularity(state.revMetricGranularity, available),
  };
}

function buildDefaultState(): FilterState {
  const asOfDate = DateTime.now().toISODate()!;
  const range = getPeriodDateRange(DEFAULT_PERIOD, DateTime.fromISO(asOfDate));
  return {
    period: DEFAULT_PERIOD,
    asOfDate,
    customStart: range.startDate,
    customEnd: range.endDate,
    scope: {
      countryCode: null,
      divisionName: null,
      groupName: null,
      locationId: null,
      resourceId: null,
    },
    appointmentTypes: [],
    drillLevel: "site",
    capacityView: "avg",
    capacityMetric: "hours",
    utilGranularity: "day",
    capGranularity: "day",
    revGranularity: "day",
    revMetricGranularity: "day",
    excludeZeroApptDays: true,
    launchedBefore: null,
  };
}

export function useFilters() {
  const [filters, setFilters] = useState<FilterState>(() => buildDefaultState());

  function setPeriod(period: PeriodKey) {
    setFilters((f) => withPeriodRange(f, period, f.asOfDate));
  }

  function setAsOfDate(asOfDate: string) {
    setFilters((f) => withPeriodRange(f, f.period, asOfDate));
  }

  function setCustomDates(startDate: string, endDate: string) {
    setFilters((f) => ({ ...f, customStart: startDate, customEnd: endDate }));
  }

  function setScope(patch: Partial<ScopeFilters>) {
    setFilters((f) => {
      const newScope = { ...f.scope, ...patch };
      // Clear downstream selections — hierarchy: Country → Group → Division → Site → DVM
      if ("countryCode" in patch) {
        newScope.groupName = null;
        newScope.divisionName = null;
        newScope.locationId = null;
        newScope.resourceId = null;
      } else if ("groupName" in patch) {
        newScope.divisionName = null;
        newScope.locationId = null;
        newScope.resourceId = null;
      } else if ("divisionName" in patch) {
        newScope.locationId = null;
        newScope.resourceId = null;
      } else if ("locationId" in patch) {
        newScope.resourceId = null;
      }
      return { ...f, scope: newScope };
    });
  }

  // Sets the full scope directly — no cascade clearing. Used by reverse-fill
  // logic in AppPage when a Site or DVM selection auto-populates parent filters.
  function setScopeRaw(scope: ScopeFilters) {
    setFilters((f) => ({ ...f, scope }));
  }

  function resetScope() {
    setFilters((f) => ({
      ...f,
      scope: {
        countryCode: null,
        divisionName: null,
        groupName: null,
        locationId: null,
        resourceId: null,
      },
    }));
  }

  function setAppointmentTypes(appointmentTypes: string[]) {
    setFilters((f) => ({ ...f, appointmentTypes }));
  }

  function setDrillLevel(drillLevel: FilterState["drillLevel"]) {
    setFilters((f) => ({ ...f, drillLevel }));
  }

  function setCapacityView(capacityView: FilterState["capacityView"]) {
    setFilters((f) => ({ ...f, capacityView }));
  }

  function setCapacityMetric(capacityMetric: FilterState["capacityMetric"]) {
    setFilters((f) => ({ ...f, capacityMetric }));
  }

  // Clamped on the way in, not just when the period changes. CapacityChart used
  // to force "day" to match a per-day metric, which could set a granularity the
  // period no longer offers -- T13W then plotted 91 daily points despite the cap
  // in getAvailableGranularities. The metric no longer does that, but the setter
  // is the right place for the guarantee.
  function clampToPeriod(g: Granularity, f: FilterState): Granularity {
    const range = { startDate: f.customStart, endDate: f.customEnd };
    return clampGranularity(g, getAvailableGranularities(range, getPeriodFamily(f.period)));
  }

  function setUtilGranularity(utilGranularity: FilterState["utilGranularity"]) {
    setFilters((f) => ({ ...f, utilGranularity: clampToPeriod(utilGranularity, f) }));
  }

  function setCapGranularity(capGranularity: FilterState["capGranularity"]) {
    setFilters((f) => ({ ...f, capGranularity: clampToPeriod(capGranularity, f) }));
  }

  function setRevGranularity(revGranularity: FilterState["revGranularity"]) {
    setFilters((f) => ({ ...f, revGranularity: clampToPeriod(revGranularity, f) }));
  }

  function setRevMetricGranularity(revMetricGranularity: FilterState["revMetricGranularity"]) {
    setFilters((f) => ({ ...f, revMetricGranularity: clampToPeriod(revMetricGranularity, f) }));
  }

  function setExcludeZeroApptDays(excludeZeroApptDays: boolean) {
    setFilters((f) => ({ ...f, excludeZeroApptDays }));
  }

  function setLaunchedBefore(launchedBefore: string | null) {
    setFilters((f) => ({ ...f, launchedBefore }));
  }

  return {
    filters,
    setPeriod,
    setScopeRaw,
    setAsOfDate,
    setCustomDates,
    setScope,
    resetScope,
    setAppointmentTypes,
    setDrillLevel,
    setCapacityView,
    setCapacityMetric,
    setUtilGranularity,
    setCapGranularity,
    setRevGranularity,
    setRevMetricGranularity,
    setExcludeZeroApptDays,
    setLaunchedBefore,
  };
}
